import type { CanvasTarget, WorkspaceView } from './types';
import { artifactCanvasScope, isArtifactCanvas } from './artifacts/routes.ts';

export const CANVAS_HISTORY_KEY = 'poneva.canvasNavigation';

export type CanvasHistoryState = {
  target: CanvasTarget | null;
  scrollTop: number;
  previous: CanvasTarget | null;
  /** The page the canvas was opened from; null on a direct link (the canvas's own kind home applies). */
  fromView: WorkspaceView | null;
};

export function canvasHistoryState(
  target: CanvasTarget | null,
  scrollTop = 0,
  previous: CanvasTarget | null = null,
  fromView: WorkspaceView | null = null,
) {
  return {
    [CANVAS_HISTORY_KEY]: {
      target: target?.kind === 'voice-preview' ? null : target,
      scrollTop: Math.max(0, scrollTop),
      previous: previous?.kind === 'voice-preview' ? null : previous,
      fromView,
    },
  };
}

export function canvasHistoryFrom(state: unknown): CanvasHistoryState | null {
  if (!state || typeof state !== 'object') return null;
  const value = (state as Record<string, unknown>)[CANVAS_HISTORY_KEY];
  if (!value || typeof value !== 'object') return null;
  const entry = value as Record<string, unknown>;
  return {
    target: (entry.target as CanvasTarget | null) ?? null,
    scrollTop: typeof entry.scrollTop === 'number' ? Math.max(0, entry.scrollTop) : 0,
    previous: (entry.previous as CanvasTarget | null) ?? null,
    fromView: typeof entry.fromView === 'string' ? (entry.fromView as WorkspaceView) : null,
  };
}

export type CanvasNavigationOptions = {
  /** Async arrivals must never replace a canvas the person is reading/editing. */
  auto?: boolean;
  /** Replace the current route, used when editing/restaging the same object. */
  replace?: boolean;
  /** Internal post-save navigation: the current editor already committed its draft. */
  committed?: boolean;
  /** The first App preview of a build replaces the Activity view the person left open (never any other canvas). */
  fromActivity?: boolean;
  /** The current scroll position is kept against the current canvas route. */
  scrollTop?: number;
};

export type CanvasNavigationState = {
  target: CanvasTarget | null;
  scrollTop: number;
};

export function sameCanvasObject(a: CanvasTarget | null, b: CanvasTarget | null): boolean {
  if (!a || !b || a.kind !== b.kind || a.id !== b.id) return false;
  return (
    !isArtifactCanvas(a) ||
    JSON.stringify(artifactCanvasScope(a)) === JSON.stringify(artifactCanvasScope(b))
  );
}

export function sameCanvasRoute(a: CanvasTarget | null, b: CanvasTarget | null): boolean {
  type Focus = CanvasTarget & {
    section?: string;
    revision?: string;
    suggestion?: string;
    rowId?: string;
  };
  const left = a as Focus | null;
  const right = b as Focus | null;
  return Boolean(
    sameCanvasObject(left, right) &&
    left?.tab === right?.tab &&
    left?.full === right?.full &&
    left?.runId === right?.runId &&
    left?.activityMessageId === right?.activityMessageId &&
    left?.activityTab === right?.activityTab &&
    left?.step === right?.step &&
    left?.section === right?.section &&
    left?.revision === right?.revision &&
    left?.rowId === right?.rowId &&
    left?.suggestion === right?.suggestion,
  );
}

/**
 * Returns whether an incoming canvas may replace the current one. The caller
 * owns the user prompt because only the mounted canvas knows its edit state.
 */
export function canAutoOpenCanvas(
  current: CanvasTarget | null,
  next: CanvasTarget | null,
  { auto = false, dirty = false, reading = false } = {},
): boolean {
  // Tabs and other subordinate views remain inside the edited object; the
  // dirty guard applies only when a different object would replace it.
  if (!auto || !next || sameCanvasObject(current, next)) return true;
  return !(dirty || reading);
}

export function restoreCanvasScroll(
  state: CanvasNavigationState | undefined,
  element: HTMLElement | null,
): void {
  if (!state || !element) return;
  element.scrollTop = state.scrollTop;
}
